import * as React from 'react';
import { ProfileType, UserProfileEntryProps, deviceTypesWithAccurateBatteryPercentage } from '@ixicare/resources';
import { IconButton, Link, formatDate, formatDateTime, getCurrentDate } from '@ixicare/ui';
import BatteryIndicator from '~components/BatteryIndicator';
import OfflineDevice from '~components/OfflineDevice/OfflineDevice';
import { StatusBadge } from '~components/Presence';
import { teams } from '~/config/profile';
import { t } from '~i18n';
import styles from './ProfileTable.module.css';
import type PresenceStore from '~/store/Presence';

export type ProfileTransformContext = {
  presence: PresenceStore;
  editPermissions?: boolean;
  canViewDevices?: boolean;
  organizationNameById: Record<string, string>;
  onRequestDeviceLink: (resident: UserProfileEntryProps) => void;
};

export const PROFILE_COLUMNS = [
  t('profiles.table.firstName'),
  t('profiles.table.lastName'),
  t('profiles.table.status'),
  t('profiles.table.created'),
  t('profiles.table.role'),
  t('global.hasAccessTo'),
  t('profiles.table.device'),
  t('profiles.table.deviceUpdatedAt'),
];

export const PROFILE_COLUMN_IDS = [
  'first_name',
  'last_name',
  'presence_state',
  'created',
  'role',
  'permission_org',
  'device',
  'device_updated_at',
];

const RESIDENT_COLUMN_IDS = ['first_name', 'last_name', 'created', 'device', 'device_updated_at'];
const HCP_COLUMN_IDS = ['first_name', 'last_name', 'presence_state', 'created', 'role', 'permission_org'];

export const RESIDENT_DEFAULT_COLUMN_IDS = RESIDENT_COLUMN_IDS;
export const HCP_DEFAULT_COLUMN_IDS = HCP_COLUMN_IDS;

const roleLabel = (profile: UserProfileEntryProps): string | undefined => {
  if (profile.profileType === ProfileType.Resident) return t('alerts.table.resident');
  return [...teams]
    .sort((a, b) => b.value.length - a.value.length)
    .find((team) => team.value.every((tm) => profile.teams?.some((pt) => pt.name === tm)))?.label;
};

function DeviceCell({
  profile,
  editPermissions,
  canViewDevices,
  onRequestDeviceLink,
}: {
  profile: UserProfileEntryProps;
  editPermissions?: boolean;
  canViewDevices?: boolean;
  onRequestDeviceLink: (r: UserProfileEntryProps) => void;
}) {
  if (profile.profileType !== ProfileType.Resident) return <>—</>;
  const device = profile.ownedDevice;
  const showBrokenBatteryWarning =
    !!device?.hoursWithLowBatteryInLast24h &&
    !!device?.maxUptimeInLast24h &&
    device.hoursWithLowBatteryInLast24h >= 6 &&
    device.maxUptimeInLast24h / 3600 >= 4;

  const inaccurate = !device?.deviceType || !deviceTypesWithAccurateBatteryPercentage.includes(device.deviceType);

  if (!device) {
    return (
      <div className={styles.DeviceCell}>
        {editPermissions ? (
          <span title={t('deviceDetail.actions.link')}>
            <IconButton icon="plus" onClick={() => onRequestDeviceLink(profile)} />
          </span>
        ) : (
          <IconButton icon="cross" />
        )}
      </div>
    );
  }

  const content = (
    <>
      {device.deviceType && (
        <img className={styles.DeviceImg} src={`/assets/${device.deviceType.toLowerCase()}@2x.png`} />
      )}
      {showBrokenBatteryWarning ? (
        <>
          <p className={styles.BrokenBatteryMark}>
            <b>!*</b>
          </p>
          <BatteryIndicator inaccurate={inaccurate} percentage={0} />
        </>
      ) : typeof device.batteryLevel === 'number' ? (
        <BatteryIndicator
          inaccurate={inaccurate}
          percentage={device.batteryLevel}
          chargerConnected={device.chargerConnected}
        />
      ) : null}
    </>
  );

  if (!canViewDevices) return <div className={styles.DeviceCell}>{content}</div>;
  return (
    <Link href={`/device-overview?q=${device.macAddress}#deviceFinder`} className={styles.DeviceCell}>
      {content}
    </Link>
  );
}

function DeviceUpdatedCell({ profile }: { profile: UserProfileEntryProps }) {
  if (profile.profileType !== ProfileType.Resident) return <>—</>;
  const updatedOn = profile.ownedDevice?.status?.updatedOn;
  if (!updatedOn) return <IconButton icon="cross" />;
  return formatDate(updatedOn) === getCurrentDate() ? (
    <>{formatDateTime(updatedOn)}</>
  ) : (
    <OfflineDevice date={updatedOn} />
  );
}

function NameCell({ profile, field }: { profile: UserProfileEntryProps; field: 'firstName' | 'lastName' }) {
  const value = profile[field];
  if (profile.profileType === ProfileType.Resident) {
    return <Link href={`/residents/${profile.id}`}>{value}</Link>;
  }
  return <>{value}</>;
}

// Column order matches PROFILE_COLUMN_IDS exactly.
export function transformProfileToRow(profile: UserProfileEntryProps, context: ProfileTransformContext): any[] {
  return [
    <NameCell key="fn" profile={profile} field="firstName" />,
    <NameCell key="ln" profile={profile} field="lastName" />,
    profile.profileType === ProfileType.Resident ? (
      '—'
    ) : (
      <StatusBadge key="status" status={context.presence.bulk[profile.id] ?? null} />
    ),
    formatDate(profile.created),
    roleLabel(profile),
    (profile.permissionOrganizationId && context.organizationNameById[profile.permissionOrganizationId]) || '—',
    <DeviceCell
      key="dev"
      profile={profile}
      editPermissions={context.editPermissions}
      canViewDevices={context.canViewDevices}
      onRequestDeviceLink={context.onRequestDeviceLink}
    />,
    <DeviceUpdatedCell key="devUp" profile={profile} />,
  ];
}
